import { useEffect, useRef, useState } from 'react';
import { Icone } from '../../design/Icone';
import {
  abrirCameraTraseira,
  ajustarCamera,
  criarDetector,
  focarDeNovo,
  lerQuadro,
  temCamera,
  type Detector,
} from '../../leitor-de-codigo';

/**
 * O LEITOR PELA CÂMERA: aponta para o código de barras e ele preenche sozinho.
 *
 * Câmera traseira + o leitor de `leitor-de-codigo` (nativo no Android, ZXing no iPhone). Página
 * sem HTTPS não tem câmera: a tela diz isso com todas as letras e oferece o caminho que sempre
 * funciona — o leitor do balcão ou digitar. Nunca finge que está lendo.
 */

/**
 * O aparelho lê código pela câmera? Basta abrir a câmera: o leitor existe em todo navegador
 * (antes iPhone/Safari ficava sem, U10). Os parâmetros existem para o teste.
 */
export function leitorDisponivel(
  nav: { mediaDevices?: { getUserMedia?: unknown } } | undefined = typeof navigator === 'undefined'
    ? undefined
    : navigator,
): boolean {
  return temCamera(nav);
}

export function Leitor({
  aoLer,
  aoFechar,
}: {
  aoLer: (codigo: string) => void;
  aoFechar: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [falha, setFalha] = useState<string | null>(
    leitorDisponivel()
      ? null
      : 'Este aparelho não tem leitor de código pela câmera. Use o leitor do balcão ou digite o código.',
  );

  useEffect(() => {
    if (falha) return;
    let fluxo: MediaStream | null = null;
    let vivo = true;
    let timer = 0;
    let detector: Detector | null = null;
    void criarDetector(['ean_13', 'ean_8', 'code_128', 'upc_a', 'upc_e'])
      .then(
        (d) => {
          detector = d;
          return abrirCameraTraseira();
        },
        (e: unknown) => {
          if (vivo)
            setFalha(
              'O leitor não carregou (sem internet?). Use o leitor do balcão ou digite o código.',
            );
          throw e;
        },
      )
      .then(async (s) => {
        fluxo = s;
        if (!vivo || !video.current) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        video.current.srcObject = s;
        await video.current.play();
        // Foco contínuo + zoom ~2× (lê a um palmo, onde o foco funciona) — dono, 29/09.
        void ajustarCamera(s);
        const d = detector;
        if (!d) return;
        const tela = document.createElement('canvas');
        let n = 0;
        const olhar = async () => {
          if (!vivo || !video.current) return;
          try {
            const c = await lerQuadro(d, video.current, tela, n++);
            if (c) {
              aoLer(c);
              return;
            }
          } catch {
            // quadro ainda sem imagem: tenta no próximo
          }
          timer = window.setTimeout(() => void olhar(), 100);
        };
        void olhar();
      })
      .catch(() => {
        if (vivo && detector)
          setFalha(
            'A câmera não abriu (permissão negada ou em uso). Use o leitor do balcão ou digite o código.',
          );
      });
    return () => {
      vivo = false;
      window.clearTimeout(timer);
      fluxo?.getTracks().forEach((t) => t.stop());
    };
  }, [aoLer, falha]);

  return (
    <div className="rv-leitor" role="dialog" aria-label="Ler código de barras">
      <div className="rv-leitor-topo">
        <span>Aponte para o código de barras</span>
        <button type="button" onClick={aoFechar} aria-label="Fechar leitor">
          <Icone nome="x" tamanho={18} />
        </button>
      </div>
      {falha ? (
        <div className="rv-leitor-falha">
          <Icone nome="warn" tamanho={20} />
          <p>{falha}</p>
          <button type="button" onClick={aoFechar}>
            Digitar o código
          </button>
        </div>
      ) : (
        <div className="rv-leitor-video">
          <video
            ref={video}
            muted
            playsInline
            aria-label="Câmera — toque para focar"
            onClick={() => {
              const f = video.current?.srcObject;
              if (f instanceof MediaStream) void focarDeNovo(f);
            }}
          />
          <div className="rv-leitor-mira" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}
